<template>
  <v-col cols="12" md="6" lg="4">
    <v-row>
      <v-col cols="12" sm="6">
        <v-datetime-picker
          :key="startPickerKey"
          ref="startDatePicker"
          v-model="startDate"
          :text-field-props="textFieldPropsStartDate"
          :date-picker-props="datePropsStartDate"
          :time-picker-props="timePropsStartDate"
          date-format="dd/MM/yyyy"
          @input="startDateTimeChanged"
        />
        <div>
          Event Start:
          <br />
          {{ clockStartTime }} {{ clockStartDate }}
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <v-datetime-picker
          :key="endPickerKey"
          ref="endDatePicker"
          v-model="endDate"
          :text-field-props="textFieldPropsEndDate"
          :date-picker-props="datePropsEndDate"
          :time-picker-props="timePropsEndDate"
          date-format="dd/MM/yyyy"
          @input="endDateTimeChanged"
        />
        <div>
          Event End:
          <br />
          {{ clockEndTime }} {{ clockEndDate }}
        </div>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import moment from "moment";
import { mapState, mapMutations } from "vuex";

export default {
  name: "DateTimeRangePicker",
  data() {
    return {
      startDate: new Date(),
      endDate: new Date(),
      clockStartTime: "",
      clockStartDate: "",
      clockEndTime: "",
      clockEndDate: "",
      startPickerKey: 0,
      endPickerKey: 0,
      textFieldPropsStartDate: {
        appendIcon: "event",
      },
      datePropsStartDate: {
        headerColor: "black",
      },
      timePropsStartDate: {
        useSeconds: false,
        ampmInTitle: true,
      },
      textFieldPropsEndDate: {
        appendIcon: "event",
      },
      datePropsEndDate: {
        headerColor: "black",
      },
      timePropsEndDate: {
        useSeconds: false,
        ampmInTitle: true,
      },
    };
  },
  computed: {
    ...mapState({
      searchStartTime: (state) => state.filter.searchStartTime,
      searchEndTime: (state) => state.filter.searchEndTime,
      globalEventFilter: (state) => state.filter.globalEventFilter,
    }),
  },
  watch: {
    startDate(newVal) {
      if (newVal) {
        let sDate = moment(newVal).format("YYYYMMDDHHmm");
        this.setSearchStartTime(sDate);
      }
    },
    endDate(newVal) {
      if (newVal) {
        let eDate = moment(newVal).format("YYYYMMDDHHmm");
        this.setSearchEndTime(eDate);
      }
    },
    searchStartTime: {
      immediate: true,
      handler(newVal, oldVal) {
        // console.log('searchStartTime', newVal, oldVal);
        if (newVal && newVal !== oldVal) {
          if (newVal !== "notSet") {
            this.startDate = moment(newVal, "YYYYMMDDHHmm").toDate();
          } else {
            this.startDate = null;
            this.forceDatePickerUpdate();
          }
        }
      },
    },
    searchEndTime: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          if (newVal !== "notSet") {
            this.endDate = moment(newVal, "YYYYMMDDHHmm").toDate();
          } else {
            this.endDate = null;
            this.forceDatePickerUpdate();
          }
        }
      },
    },
    "globalEventFilter.EventId": {
      immediate: true,
      handler(newVal, oldVal) {
        // If the event id actually changes
        if (newVal && oldVal && newVal !== oldVal) {
          // First reset the internal state
          this.setSearchStartTime("notSet");
          this.setSearchEndTime("notSet");

          // Then immediately update with new event times
          if (this.globalEventFilter.startDateTime) {
            this.clockStartTime = moment(
              this.globalEventFilter.startDateTime,
              "YYYYMMDDHHmm"
            ).format("h:mm A");
            this.clockStartDate = moment(
              this.globalEventFilter.startDateTime,
              "YYYYMMDDHHmm"
            ).format("DD MMM YY");
            this.startDate = moment(
              this.globalEventFilter.startDateTime,
              "YYYYMMDDHHmm"
            ).toDate();
          } else {
            this.startDate = null;
            this.forceDatePickerUpdate();
          }

          if (this.globalEventFilter.endDateTime) {
            this.clockEndTime = moment(
              this.globalEventFilter.endDateTime,
              "YYYYMMDDHHmm"
            ).format("h:mm A");
            this.clockEndDate = moment(
              this.globalEventFilter.endDateTime,
              "YYYYMMDDHHmm"
            ).format("DD MMM YY");
            this.endDate = moment(
              this.globalEventFilter.endDateTime,
              "YYYYMMDDHHmm"
            ).toDate();
          } else {
            this.endDate = null;
            this.forceDatePickerUpdate();
          }
          this.$emit("event-changed");
        }
        // If just updating display (e.g. changing tabs)
        else if (newVal && newVal !== oldVal) {
          if (this.globalEventFilter.startDateTime) {
            this.clockStartTime = moment(
              this.globalEventFilter.startDateTime,
              "YYYYMMDDHHmm"
            ).format("h:mm A");
            this.clockStartDate = moment(
              this.globalEventFilter.startDateTime,
              "YYYYMMDDHHmm"
            ).format("DD MMM YY");
          }
          if (this.globalEventFilter.endDateTime) {
            this.clockEndTime = moment(
              this.globalEventFilter.endDateTime,
              "YYYYMMDDHHmm"
            ).format("h:mm A");
            this.clockEndDate = moment(
              this.globalEventFilter.endDateTime,
              "YYYYMMDDHHmm"
            ).format("DD MMM YY");
          }
        }
      },
    },
  },
  created() {
    // Initialize dates from searchStartTime and searchEndTime if they exist
    this.startDate =
      this.searchStartTime !== "notSet" && this.searchStartTime
        ? moment(this.searchStartTime, "YYYYMMDDHHmm").toDate()
        : null;
    this.endDate =
      this.searchEndTime !== "notSet" && this.searchEndTime
        ? moment(this.searchEndTime, "YYYYMMDDHHmm").toDate()
        : null;
  },
  methods: {
    ...mapMutations("filter", ["setSearchStartTime", "setSearchEndTime"]),
    forceDatePickerUpdate() {
      this.startPickerKey += 1;
      this.endPickerKey += 1;
    },
    startDateTimeChanged() {
      if (!this.startDate) {
        this.setSearchStartTime("notSet");
      }
      this.$emit("start-date-changed");
    },
    endDateTimeChanged() {
      if (!this.endDate) {
        this.setSearchEndTime("notSet");
      }
      this.$emit("end-date-changed");
    },
  },
};
</script>
